import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { logout } from '../store/slices/authSlice';
import { signOutPortalUser } from '../services/guestPortalSupabase';

const MarinaNavigation: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const isGuest = isAuthenticated && user?.role !== 'admin';
  const [brandLogoSrc, setBrandLogoSrc] = useState('/main-site/images/resort-logo.jpg');

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await signOutPortalUser().catch(() => undefined);
    dispatch(logout());
    setDropdownOpen(false);
    navigate('/booking-system/login');
  };

  return (
    <nav className="main-site-nav">
      <div className="back-to-main-site">
        <a
          href="/index.html"
          className="back-to-main-site"
          title="Return to Marina Glen main website"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Main Site
        </a>
      </div>

      <div className="booking-brand">
        <img
          src={brandLogoSrc}
          alt="Marina Glen Resort Logo"
          className="nav-logo-img"
          onError={() => {
            setBrandLogoSrc((current) => {
              if (current === '/main-site/images/resort-logo.jpg') {
                return '/resort-logo.jpg';
              }

              if (current === '/resort-logo.jpg') {
                return '/main-site/images/Resort%20Logo%201.jpg';
              }

              return current;
            });
          }}
        />
        <h2>Marina Glen Bookings</h2>
      </div>

      <div className="nav-actions">
        {isGuest ? (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            {/* Guest account button */}
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '999px',
                padding: '0.45rem 1rem 0.45rem 0.6rem',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.2s',
              }}
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
            >
              <span style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
              }}>
                {user?.firstName ? user.firstName[0].toUpperCase() : '?'}
              </span>
              <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.firstName || 'My Account'}
              </span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transition: 'transform 0.2s', transform: dropdownOpen ? 'rotate(180deg)' : 'none' }}>
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: 200,
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                zIndex: 200,
              }}>
                {/* User info header */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2, wordBreak: 'break-all' }}>
                    {user?.email}
                  </div>
                </div>

                {/* Menu items */}
                {[
                  { to: '/booking-system/dashboard', icon: '🏠', label: 'Dashboard' },
                  { to: '/booking-system/my-bookings', icon: '📅', label: 'My Bookings' },
                  { to: '/booking-system/settings', icon: '⚙️', label: 'Account Settings' },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setDropdownOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.65rem',
                      padding: '11px 16px',
                      color: '#1e293b',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f0f9ff'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <span>{item.icon}</span>
                    {item.label}
                  </Link>
                ))}

                {/* Sign out */}
                <div style={{ borderTop: '1px solid #f1f5f9' }}>
                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.65rem',
                      width: '100%',
                      padding: '11px 16px',
                      background: 'transparent',
                      border: 'none',
                      color: '#dc2626',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#fef2f2'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <span>🚪</span>
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <a href="/booking-system/login" className="admin-login-link" title="Sign In">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13.8 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Sign In
          </a>
        )}
      </div>
    </nav>
  );
};

export default MarinaNavigation;